// First: no schema may be built before Zod is told not to use eval.
import "./lib/zodNoEval";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BookingApp } from "./booking/BookingApp";
import {
  clinicSlugFromBookingPath,
  isScheduledIntakePath,
  rendersPatientEntry,
  scheduledIntakeTokenFromPath,
} from "./booking/routing";
import { PublicDocumentLink } from "./document/PublicDocumentLink";
import { PublicPatientLane } from "./intake/PublicPatientLane";
import { removeAddressTokens, takePublicPatientLink } from "./intake/publicPatientLink";
import { ScheduledIntakeApp } from "./intake/ScheduledIntakeApp";
import { PatientEntryApp } from "./PatientEntryApp";
import "./styles.css";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 60_000, retry: 1, refetchOnWindowFocus: false } },
});
const root = document.getElementById("root");
if (!root) throw new Error("Application root was not found");

const path = window.location.pathname.replace(/\/+$/, "") || "/";
const query = new URLSearchParams(window.location.search);
const demoEnabled = import.meta.env.VITE_DEMO_SCHEDULING_ENABLED === "true";
// Every page drops a link token from its address before anything renders.
const publicLink = demoEnabled ? null : takePublicPatientLink();
if (demoEnabled) removeAddressTokens();
// A secure link's page takes a later token itself (PublicPatientLane); any other page has no
// use for one and only removes it.
if (publicLink === null) window.addEventListener("hashchange", removeAddressTokens);
const clinicSlug = clinicSlugFromBookingPath(path);
const content = publicLink ? (
  publicLink.purpose === "document" ? (
    <PublicDocumentLink link={publicLink} />
  ) : (
    <PublicPatientLane link={publicLink} />
  )
) : demoEnabled && isScheduledIntakePath(path) ? (
  <ScheduledIntakeApp token={scheduledIntakeTokenFromPath(path)} />
) : rendersPatientEntry(path, demoEnabled) ? (
  <PatientEntryApp />
) : (
  <BookingApp embedded={query.get("embed") === "1"} {...(clinicSlug ? { clinicSlug } : {})} />
);

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>{content}</QueryClientProvider>
  </StrictMode>,
);

if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js");
  });
}
